import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Package, MapPin, Truck, CheckCircle2, Circle, MessageCircle,
  AlertTriangle, Star, ExternalLink, Loader2,
} from "lucide-react";
import { getCustomerOrder } from "../lib/api";
import { eur } from "../config";

const STATUS_COLOR = {
  green: "bg-green-50 text-green-700 border-green-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  orange: "bg-amber-50 text-amber-700 border-amber-200",
  yellow: "bg-yellow-50 text-yellow-700 border-yellow-200",
  red: "bg-red-50 text-red-600 border-red-200",
};
const fmt = (v) => (v ? new Date(v).toLocaleString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : null);
const WA = "590690000000"; // numéro SAV WhatsApp (placeholder)

export default function OrderTracking() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    getCustomerOrder(orderNumber).then(setO).catch(() => setErr(true));
  }, [orderNumber]);

  if (err) return (
    <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="order-not-found">
      <p className="text-ardoise mb-4">Commande introuvable.</p>
      <Link to="/compte" className="text-dore underline">Retour à mon compte</Link>
    </div>
  );
  if (!o) return <div className="min-h-[50vh] flex items-center justify-center text-ardoise"><Loader2 className="animate-spin mr-2" size={18} /> Chargement...</div>;

  const waMsg = encodeURIComponent(`Bonjour, j'ai une question sur ma commande ${o.order_number}.`);

  const TERMINAL = {
    ON_HOLD: { color: "bg-amber-50 border-amber-200 text-amber-800", icon: <AlertTriangle size={18} />, title: "Commande en attente", text: "Un souci est survenu (souvent une rupture de stock). Notre équipe vous contacte très vite — aucune action requise de votre part." },
    CANCELLED: { color: "bg-red-50 border-red-200 text-red-700", icon: <AlertTriangle size={18} />, title: "Commande annulée", text: "Cette commande a été annulée. En cas de question, contactez-nous via WhatsApp." },
    REFUNDED: { color: "bg-red-50 border-red-200 text-red-700", icon: <AlertTriangle size={18} />, title: "Commande remboursée", text: "Cette commande a été remboursée. Le montant réapparaît sous 5 à 10 jours sur votre moyen de paiement." },
  };
  const term = TERMINAL[o.status];

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="order-tracking-page">
      <button onClick={() => navigate("/compte")} className="flex items-center gap-2 text-sm text-ardoise hover:text-encre mb-6" data-testid="back-to-account"><ArrowLeft size={16} /> Mes commandes</button>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-8">
        <div>
          <h1 className="font-display text-3xl text-encre">Commande {o.order_number}</h1>
          <p className="text-sm text-ardoise/70">Passée le {fmt(o.created_at)}</p>
        </div>
        <span className={`text-xs px-3 py-1.5 uppercase tracking-wider border ${STATUS_COLOR[o.status_color] || STATUS_COLOR.blue}`} data-testid="tracking-status-badge">{o.status_label}</span>
      </div>

      {term && (
        <div className={`border p-4 mb-6 flex items-start gap-3 ${term.color}`} data-testid="tracking-terminal-banner">
          <span className="mt-0.5">{term.icon}</span>
          <div><p className="font-medium text-sm">{term.title}</p><p className="text-sm opacity-90">{term.text}</p></div>
        </div>
      )}

      {/* Timeline (flux normal uniquement) */}
      {!term && (
      <div className="bg-white border border-dore/20 p-6 mb-6" data-testid="tracking-timeline">
        <h2 className="font-display text-lg text-encre mb-5 flex items-center gap-2"><Package size={17} className="text-dore" /> Suivi de la commande</h2>
        <div className="space-y-0">
          {o.timeline.map((s, i) => (
            <div key={s.code + i} className="flex gap-3">
              <div className="flex flex-col items-center">
                {s.done ? <CheckCircle2 size={20} className="text-dore" /> : <Circle size={20} className={s.current ? "text-dore" : "text-gray-300"} />}
                {i < o.timeline.length - 1 && <div className={`w-0.5 flex-1 min-h-[28px] ${s.done ? "bg-dore" : "bg-gray-200"}`} />}
              </div>
              <div className={`pb-4 ${s.current ? "font-medium" : ""}`}>
                <p className={`text-sm ${s.done || s.current ? "text-encre" : "text-ardoise/40"}`}>{s.label} {s.current && <span className="text-dore text-xs">(en cours)</span>}</p>
                {s.at && <p className="text-xs text-ardoise/50">{fmt(s.at)}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Tracking colis */}
      {o.tracking && (
        <div className="bg-white border border-dore/20 p-6 mb-6" data-testid="tracking-parcel">
          <h2 className="font-display text-lg text-encre mb-3 flex items-center gap-2"><Truck size={17} className="text-dore" /> Suivi du colis</h2>
          <p className="text-sm text-ardoise">Transporteur : <b className="text-encre">{o.tracking.carrier}</b></p>
          <p className="text-sm text-ardoise">N° de suivi : <b className="text-encre">{o.tracking.number}</b></p>
          {o.tracking.estimated_delivery && <p className="text-sm text-ardoise">Livraison estimée : {fmt(o.tracking.estimated_delivery)}</p>}
          <div className="flex flex-wrap gap-2 mt-4">
            {o.tracking.url && <a href={o.tracking.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 bg-noir text-white px-4 py-2 text-xs uppercase tracking-wider hover:bg-encre transition-colors" data-testid="track-carrier"><ExternalLink size={13} /> Suivre le colis</a>}
            <a href={`https://t.17track.net/fr#nums=${o.tracking.number}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 border border-dore/30 px-4 py-2 text-xs uppercase tracking-wider hover:bg-dore/10 transition-colors" data-testid="track-17track"><ExternalLink size={13} /> 17track.net</a>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-6 mb-6">
        {/* Livraison */}
        <div className="bg-white border border-dore/20 p-6" data-testid="tracking-shipping">
          <h2 className="font-display text-base text-encre mb-3 flex items-center gap-2"><MapPin size={16} className="text-dore" /> Livraison</h2>
          <p className="text-sm text-encre">{o.shipping.name}</p>
          <p className="text-sm text-ardoise/80">{o.shipping.address}</p>
          <p className="text-sm text-ardoise/80">{o.shipping.zip} {o.shipping.city}</p>
          <p className="text-sm text-ardoise/80">{o.shipping.region}, {o.shipping.country}</p>
          <p className="text-sm text-ardoise/60 mt-2">{o.shipping.phone}</p>
          <p className="text-sm text-ardoise/60">{o.shipping.email}</p>
        </div>
        {/* Récap financier */}
        <div className="bg-white border border-dore/20 p-6" data-testid="tracking-summary">
          <h2 className="font-display text-base text-encre mb-3">Récapitulatif</h2>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between text-ardoise"><span>Sous-total</span><span>{eur(o.subtotal)}</span></div>
            {o.discount > 0 && <div className="flex justify-between text-green-700"><span>Réduction</span><span>-{eur(o.discount)}</span></div>}
            <div className="flex justify-between text-ardoise"><span>Livraison</span><span>{o.shipping_cost > 0 ? eur(o.shipping_cost) : "Offerte"}</span></div>
            <div className="flex justify-between text-encre font-medium pt-2 border-t border-dore/10"><span>Total payé</span><span>{eur(o.total)}</span></div>
          </div>
        </div>
      </div>

      {/* Produits */}
      <div className="bg-white border border-dore/20 p-6 mb-6" data-testid="tracking-items">
        <h2 className="font-display text-base text-encre mb-4">Articles commandés</h2>
        {o.items.map((it, i) => (
          <div key={i} className="flex items-center gap-4 py-3 border-b border-dore/10 last:border-0">
            {it.image && <img src={it.image} alt={it.name} className="w-14 h-16 object-cover" referrerPolicy="no-referrer" />}
            <div className="flex-1">
              <p className="text-sm text-encre">{it.name}</p>
              <p className="text-xs text-ardoise/60">{[it.size, it.color].filter(Boolean).join(" · ")}</p>
            </div>
            <div className="text-right text-sm">
              <p className="text-ardoise">{it.quantity} × {eur(it.price)}</p>
              <p className="text-encre font-medium">{eur(it.line_total)}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Actions client */}
      <div className="flex flex-wrap gap-2" data-testid="tracking-actions">
        <a href={`https://wa.me/${WA}?text=${waMsg}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 bg-[#25D366] text-white px-4 py-2.5 text-xs uppercase tracking-wider hover:opacity-90 transition-opacity" data-testid="action-sav"><MessageCircle size={14} /> Contacter le SAV</a>
        <a href={`https://wa.me/${WA}?text=${encodeURIComponent(`Problème avec la commande ${o.order_number}`)}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 border border-red-200 text-red-600 px-4 py-2.5 text-xs uppercase tracking-wider hover:bg-red-50 transition-colors" data-testid="action-problem"><AlertTriangle size={14} /> Signaler un problème</a>
        {o.is_delivered && (
          <>
            <Link to="/boutique" className="flex items-center gap-1.5 border border-dore/30 px-4 py-2.5 text-xs uppercase tracking-wider hover:bg-dore/10 transition-colors" data-testid="action-return">Retour / Échange</Link>
            <Link to="/boutique" className="flex items-center gap-1.5 bg-dore text-noir px-4 py-2.5 text-xs uppercase tracking-wider hover:opacity-90 transition-opacity" data-testid="action-review"><Star size={14} /> Laisser un avis</Link>
          </>
        )}
      </div>
    </div>
  );
}
